<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <div
      ref="messagesContainer"
      class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1"
    >
      <div
        v-if="isFetching"
        class="text-n-slate-10 text-center py-10 text-sm"
      >
        <div class="inline-block size-5 border-2 border-n-slate-8 border-t-n-brand rounded-full animate-spin" />
        <p class="mt-2">Carregando mensagens...</p>
      </div>
      <div
        v-else-if="messages.length === 0"
        class="flex-1 flex flex-col items-center justify-center py-10"
      >
        <div class="text-5xl mb-3">💬</div>
        <p class="text-n-slate-10 text-sm">
          Nenhuma mensagem ainda
        </p>
        <p class="text-n-slate-9 text-xs mt-1">Diga olá!</p>
      </div>
      <template v-else>
        <template
          v-for="(msg, index) in messages"
          :key="msg.id"
        >
          <div
            v-if="showDateSeparator(index)"
            class="flex items-center gap-3 my-3"
          >
            <div class="flex-1 h-px bg-n-weak" />
            <span class="text-[11px] text-n-slate-9 font-medium px-2">
              {{ formatDate(msg.created_at) }}
            </span>
            <div class="flex-1 h-px bg-n-weak" />
          </div>

          <div
            v-if="msg.content === '🔔 CHAMOU SUA ATENÇÃO!'"
            class="self-center my-2"
          >
            <div
              class="px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold text-center shadow-sm"
            >
              🔔
              {{
                msg.user_id === currentUserId
                  ? 'Você chamou a atenção'
                  : (msg.sender_name || 'Alguém') +
                    ' chamou sua atenção!'
              }}
            </div>
          </div>

          <div
            v-else
            class="flex gap-2 max-w-[85%]"
            :class="
              msg.user_id === currentUserId
                ? 'self-end flex-row-reverse'
                : 'self-start'
            "
          >
            <div
              v-if="msg.user_id !== currentUserId && showAvatar(index)"
              class="flex-shrink-0 self-end"
            >
              <img
                v-if="msg.sender_avatar && !brokenAvatars.has(msg.sender_avatar)"
                :src="msg.sender_avatar"
                class="size-7 rounded-full object-cover"
                @error="onAvatarError(msg.sender_avatar)"
              />
              <div
                v-else
                class="size-7 rounded-full text-white flex items-center justify-center font-semibold text-xs"
                style="background: linear-gradient(135deg, #3b82f6, #4f46e5);"
              >
                {{ msg.sender_name?.charAt(0)?.toUpperCase() }}
              </div>
            </div>
            <div
              v-else-if="msg.user_id !== currentUserId"
              class="w-7 flex-shrink-0"
            />

            <div>
              <p
                v-if="
                  msg.user_id !== currentUserId && showAvatar(index)
                "
                class="text-[11px] text-n-slate-10 mb-0.5 ml-1 m-0"
              >
                {{ msg.sender_name }}
              </p>
              <div
                class="px-3.5 py-2.5 rounded-2xl break-words"
                :class="
                  msg.user_id === currentUserId
                    ? 'rounded-br-md text-white'
                    : 'rounded-bl-md bg-n-alpha-2 text-n-slate-12'
                "
                :style="
                  msg.user_id === currentUserId
                    ? 'background: linear-gradient(135deg, #6366f1, #4f46e5);'
                    : undefined
                "
              >
                <p
                  class="m-0 text-sm leading-relaxed whitespace-pre-wrap"
                >
                  {{ msg.content }}
                </p>
                <span
                  class="text-[10px] block text-right mt-1"
                  :class="
                    msg.user_id === currentUserId
                      ? 'text-white/55'
                      : 'text-n-slate-9'
                  "
                >
                  {{ formatTime(msg.created_at) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div class="flex flex-col px-4 py-3 gap-2 border-t border-n-weak">
      <textarea
        ref="inputRef"
        v-model="messageText"
        class="w-full bg-n-alpha-black2 outline outline-1 outline-n-weak rounded-xl text-n-slate-12 px-3.5 py-2.5 text-sm resize-none leading-snug max-h-[120px] placeholder:text-n-slate-10 focus:outline-n-brand transition-all"
        placeholder="Digite sua mensagem..."
        rows="1"
        @keydown.enter.exact.prevent="send"
        @input="autoResize"
      />
      <div class="flex items-center justify-between">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 text-amber-700 dark:text-amber-400 hover:from-amber-200 hover:to-orange-200 dark:hover:from-amber-900/50 dark:hover:to-orange-900/50 transition-all"
          :disabled="isNudging"
          @click="sendNudge"
        >
          🔔
          {{ isNudging ? '...' : 'Atenção' }}
        </button>
        <button
          class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!messageText.trim() || isSending"
          @click="send"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12.5 1.5L6.5 7.5M12.5 1.5L8.5 12.5L6.5 7.5L1.5 5.5L12.5 1.5Z" />
          </svg>
          Enviar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const store = useStore();
const messageText = ref('');
const messagesContainer = ref(null);
const inputRef = ref(null);
const isNudging = ref(false);
const brokenAvatars = reactive(new Set());

function onAvatarError(url) {
  if (url) brokenAvatars.add(url);
}

const currentUserId = computed(
  () => store.getters.getCurrentUser?.id
);
const messages = computed(
  () => store.getters['internalChat/getActiveMessages']
);
const isFetching = computed(
  () => store.getters['internalChat/getUIFlags'].isFetchingMessages
);
const isSending = computed(
  () => store.getters['internalChat/getUIFlags'].isSending
);

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year:
      d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

function showDateSeparator(index) {
  if (index === 0) return true;
  const curr = new Date(messages.value[index].created_at);
  const prev = new Date(messages.value[index - 1].created_at);
  return curr.toDateString() !== prev.toDateString();
}

function showAvatar(index) {
  const msg = messages.value[index];
  if (index === 0) return true;
  const prev = messages.value[index - 1];
  return prev.user_id !== msg.user_id;
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: 'smooth',
      });
    }
  });
}

async function send() {
  const content = messageText.value.trim();
  if (!content) return;
  messageText.value = '';
  await store.dispatch('internalChat/sendMessage', {
    conversationId: props.conversationId,
    content,
  });
  scrollToBottom();
  if (inputRef.value) {
    inputRef.value.style.height = 'auto';
  }
}

async function sendNudge() {
  isNudging.value = true;
  try {
    await store.dispatch('internalChat/sendMessage', {
      conversationId: props.conversationId,
      content: '🔔 CHAMOU SUA ATENÇÃO!',
    });
    scrollToBottom();
  } finally {
    isNudging.value = false;
  }
}

function autoResize(e) {
  const el = e.target;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

watch(messages, () => scrollToBottom(), { deep: true });

onMounted(() => {
  scrollToBottom();
  inputRef.value?.focus();
});
</script>
